import { layoutKeysForNode, SERVICE_GRID_SPANS } from '../admin/visual-canvas/canvasCapabilities';

export const LANDING_SCHEMA_VERSION = 4;
export const MANDATORY_LANDING_STRUCTURES = Object.freeze([
  'navigation', 'hero', 'services', 'footer',
]);

export const LANDING_EDITOR_NODE_IDS = Object.freeze([
  'navigation', 'navigation.brand', 'navigation.login', 'navigation.register',
  'hero', 'hero.title', 'hero.subtitle', 'hero.search', 'hero.primary_cta', 'hero.image',
  'services', 'service:taxi', 'service:delivery', 'service:restaurant',
  'benefits', 'benefit:security', 'benefit:payments', 'benefit:protection', 'benefit:support',
  'providers', 'providers.title', 'providers.description', 'providers.actions', 'providers.image',
  'footer',
]);

export const EMPTY_EDITOR_LAYOUT = Object.freeze({ desktop: {}, mobile: {} });
export const DEFAULT_ELEMENT_VISIBILITY = Object.freeze({
  'hero.subtitle': true,
  'hero.search': true,
  'hero.image': true,
  'providers.title': true,
  'providers.description': true,
  'providers.actions': true,
  'providers.image': true,
});

export const LANDING_ACTIONS = Object.freeze({
  go_login: { href: '/login' },
  go_register_client: { href: '/register?role=client' },
  go_register_provider: { href: '/register?role=provider' },
  go_provider_login: { href: '/login?role=provider' },
  go_provider_requirements: { href: '/register?role=provider' },
  go_admin_access: { href: '/admin' },
  scroll_services: { href: '#servicios' },
  scroll_providers: { href: '#proveedores' },
  open_support: { href: '#soporte', externalAllowed: true },
  open_terms: { href: '#terminos', externalAllowed: true },
  open_privacy: { href: '#privacidad', externalAllowed: true }
});

export const ALLOWED_LANDING_ACTIONS = Object.freeze(Object.keys(LANDING_ACTIONS));

const defaultImage = {
  desktop_url: '',
  mobile_url: '',
  position_x: 50,
  position_y: 50,
  scale: 1,
  fit: 'cover',
  radius: 24
};

export const DEFAULT_LANDING_CONTENT = Object.freeze({
  schema_version: LANDING_SCHEMA_VERSION,
  section_order: ['hero', 'services', 'benefits', 'providers'],
  editor_layout: { desktop: {}, mobile: {} },
  element_visibility: { ...DEFAULT_ELEMENT_VISIBILITY },
  navigation: {
    visible: true,
    brand_name: 'PROVI',
    logo_url: '',
    login_text: 'Iniciar sesión',
    register_text: 'Registrarse',
    background_color: '#07080A',
    spacing: {
      desktop: { horizontal: 24, vertical: 16 },
      mobile: { horizontal: 24, vertical: 16 }
    }
  },
  hero: {
    visible: true,
    title: 'Todo Santa Rosa',
    highlighted_text: 'a un clic de distancia.',
    subtitle: 'Conectamos la ciudad con servicios verificados de transporte, delivery y comida. Rápido, seguro y local.',
    background_color: '#07080A',
    image: { ...defaultImage },
    primary_cta: { text: 'Buscar', action: 'go_register_client', url: '' },
    service_prompt: 'Pedir un Taxi...',
    location_text: 'Santa Rosa, El Oro'
  },
  services: {
    visible: true,
    background_color: '#08090C',
    gap: { desktop: 16, mobile: 16 },
    cards: [
      {
        id: 'taxi', title: 'Taxi Provi',
        description: 'Conductores profesionales con PIN de seguridad y rastreo en tiempo real.',
        button_text: 'Pedir ahora →', action: 'go_register_client', url: '',
        image_url: '', image_position_x: 50, image_position_y: 50, image_scale: 1, image_fit: 'cover',
        accent_color: '#00F0FF', icon: 'square', background_color: '#111217', border_color: '#00F0FF',
        radius: 16, padding: 20, visible: true
      },
      {
        id: 'delivery', title: 'Delivery',
        description: 'Envíos urbanos en minutos. Documentos, paquetes y trámites locales.',
        button_text: 'Enviar algo →', action: 'go_register_client', url: '',
        image_url: '', image_position_x: 50, image_position_y: 50, image_scale: 1, image_fit: 'cover',
        accent_color: '#FF007F', icon: 'circle', background_color: '#111217', border_color: '#FF007F',
        radius: 16, padding: 20, visible: true
      },
      {
        id: 'restaurant', title: 'Restaurant',
        description: 'Tus restaurantes favoritos de Santa Rosa directo a tu mesa.',
        button_text: 'Explorar menú →', action: 'go_register_client', url: '',
        image_url: '', image_position_x: 50, image_position_y: 50, image_scale: 1, image_fit: 'cover',
        accent_color: '#FBBF24', icon: 'ring', background_color: '#111217', border_color: '#FBBF24',
        radius: 16, padding: 20, visible: true
      }
    ]
  },
  benefits: {
    visible: true,
    background_color: '#0B0C10',
    items: [
      { id: 'security', label: 'Seguridad', value: 'Proveedores Verificados', color: '#00F0FF', visible: true },
      { id: 'payments', label: 'Pagos', value: 'Efectivo o Transferencia', color: '#FF007F', visible: true },
      { id: 'protection', label: 'Protección', value: 'PIN de Seguridad', color: '#00F0FF', visible: true },
      { id: 'support', label: 'Cercanía', value: 'Soporte Local 24/7', color: '#FF007F', visible: true }
    ]
  },
  providers: {
    visible: true,
    title: '¿Tienes un negocio o vehículo?',
    description: 'Únete a la red de proveedores más confiable de Santa Rosa y comienza a recibir pedidos hoy mismo.',
    register_text: 'Registrarme como Proveedor',
    login_text: 'Iniciar sesión',
    requirements_text: 'Solicitar información',
    register_action: 'go_register_provider',
    login_action: 'go_provider_login',
    requirements_action: 'go_provider_requirements',
    background_color: '#FF007F',
    image: { ...defaultImage }
  },
  footer: {
    visible: true,
    legal_text: '© 2026 PROVI Santa Rosa · El Oro',
    terms_text: 'Términos',
    privacy_text: 'Privacidad',
    support_text: 'Panel Admin',
    terms_action: 'open_terms',
    privacy_action: 'open_privacy',
    support_action: 'go_admin_access',
    terms_url: '', privacy_url: '', support_url: '',
    instagram_url: '', facebook_url: '', tiktok_url: '',
    background_color: '#07080A'
  },
  layout_spacing: {
    desktop: {
      section_padding_x: 24, section_padding_y: 56,
      hero_services_gap: 0, services_gap: 16,
      benefits_margin: 0, providers_margin: 0
    },
    mobile: {
      section_padding_x: 24, section_padding_y: 56,
      hero_services_gap: 0, services_gap: 16,
      benefits_margin: 0, providers_margin: 0
    }
  }
});

const clone = (value) => JSON.parse(JSON.stringify(value));
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
const finiteNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : null;

const sanitizeEditorLayout = (layout) => {
  const source = layout && typeof layout === 'object' ? layout : {};
  return ['desktop', 'mobile'].reduce((result, device) => {
    const deviceSource = source[device] && typeof source[device] === 'object' ? source[device] : {};
    result[device] = LANDING_EDITOR_NODE_IDS.reduce((nodes, id) => {
      const raw = deviceSource[id];
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return nodes;
      const allowedKeys = new Set(layoutKeysForNode(id));
      const limits = device === 'mobile' ? { x: 40, y: 64 } : { x: 160, y: 120 };
      const next = {};
      const offsetX = finiteNumber(raw.offset_x);
      const offsetY = finiteNumber(raw.offset_y);
      const width = finiteNumber(raw.width_percent);
      const minHeight = finiteNumber(raw.min_height);
      const paddingTop = finiteNumber(raw.padding_top);
      const paddingBottom = finiteNumber(raw.padding_bottom);
      const maxWidth = finiteNumber(raw.max_width);
      const order = finiteNumber(raw.order);
      const frameWidth = finiteNumber(raw.image_frame_width);
      const frameHeight = finiteNumber(raw.image_frame_height);
      const gridSpan = finiteNumber(raw.grid_span);
      const gridStart = finiteNumber(raw.grid_start);
      if (allowedKeys.has('offset_x') && offsetX !== null) next.offset_x = Math.round(clamp(offsetX, -limits.x, limits.x));
      if (allowedKeys.has('offset_y') && offsetY !== null) next.offset_y = Math.round(clamp(offsetY, -limits.y, limits.y));
      if (allowedKeys.has('width_percent') && width !== null) next.width_percent = Math.round(clamp(width, 15, 100));
      if (allowedKeys.has('min_height') && minHeight !== null) next.min_height = Math.round(clamp(minHeight, 0, 1200));
      if (allowedKeys.has('max_width') && maxWidth !== null) next.max_width = Math.round(clamp(maxWidth, 72, 1600));
      if (allowedKeys.has('order') && order !== null) next.order = Math.round(clamp(order, 0, 20));
      if (allowedKeys.has('image_frame_width') && frameWidth !== null) next.image_frame_width = Math.round(clamp(frameWidth, 48, 1600));
      if (allowedKeys.has('image_frame_height') && frameHeight !== null) next.image_frame_height = Math.round(clamp(frameHeight, 48, 1200));
      if (allowedKeys.has('padding_top') && paddingTop !== null) next.padding_top = Math.round(clamp(paddingTop, 0, 240));
      if (allowedKeys.has('padding_bottom') && paddingBottom !== null) next.padding_bottom = Math.round(clamp(paddingBottom, 0, 240));
      if (allowedKeys.has('grid_span') && SERVICE_GRID_SPANS.includes(gridSpan)) next.grid_span = gridSpan;
      if (allowedKeys.has('grid_start') && gridStart !== null) next.grid_start = Math.round(clamp(gridStart, 1, 13 - Number(next.grid_span || 1)));
      if (allowedKeys.has('align') && ['left', 'center', 'right'].includes(raw.align)) next.align = raw.align;
      if (Object.keys(next).length) nodes[id] = next;
      return nodes;
    }, {});
    return result;
  }, {});
};

const mergeObject = (defaults, incoming) => {
  if (Array.isArray(defaults)) return Array.isArray(incoming) ? clone(incoming) : clone(defaults);
  if (!defaults || typeof defaults !== 'object') return incoming === undefined ? defaults : incoming;
  const source = incoming && typeof incoming === 'object' && !Array.isArray(incoming) ? incoming : {};
  return Object.keys(defaults).reduce((result, key) => {
    result[key] = mergeObject(defaults[key], source[key]);
    return result;
  }, { ...source });
};

const migrateLegacy = (raw, normalized) => {
  if (!raw || Number(raw.schema_version || 1) >= LANDING_SCHEMA_VERSION) return normalized;
  const next = clone(normalized);
  if (raw.brand_name) next.navigation.brand_name = raw.brand_name;
  if (raw.logo_url) next.navigation.logo_url = raw.logo_url;
  if (raw.hero_title && raw.hero_title !== 'Taxi, delivery y comida en Santa Rosa') next.hero.title = raw.hero_title;
  if (raw.hero_subtitle && raw.hero_subtitle !== 'Servicios locales con proveedores verificados cerca de ti.') next.hero.subtitle = raw.hero_subtitle;
  if (raw.hero_image_desktop_url) next.hero.image.desktop_url = raw.hero_image_desktop_url;
  if (raw.hero_image_mobile_url) next.hero.image.mobile_url = raw.hero_image_mobile_url;
  const legacyImages = [raw.taxi_image_url, raw.delivery_image_url, raw.food_image_url];
  next.services.cards = next.services.cards.map((card, index) => legacyImages[index] ? { ...card, image_url: legacyImages[index] } : card);
  if (raw.verified_provider_image_url) next.providers.image.desktop_url = raw.verified_provider_image_url;
  if (raw.show_provider_section === false) next.providers.visible = false;
  return next;
};

export const normalizeLandingContent = (raw = {}) => {
  const merged = mergeObject(DEFAULT_LANDING_CONTENT, raw);
  const migrated = migrateLegacy(raw, merged);
  migrated.schema_version = LANDING_SCHEMA_VERSION;
  migrated.editor_layout = sanitizeEditorLayout(raw.editor_layout || migrated.editor_layout);
  migrated.section_order = [...new Set((migrated.section_order || []).filter((item) => ['hero', 'services', 'benefits', 'providers'].includes(item)))];
  ['hero', 'services', 'benefits', 'providers'].forEach((section) => {
    if (!migrated.section_order.includes(section)) migrated.section_order.push(section);
  });
  MANDATORY_LANDING_STRUCTURES.forEach((section) => {
    migrated[section].visible = true;
  });
  return migrated;
};

export const resolveLandingAction = (action, url = '') => {
  const definition = LANDING_ACTIONS[action];
  if (!definition) return { available: false, href: '#', external: false };
  const externalUrl = String(url || '').trim();
  if (externalUrl && definition.externalAllowed && /^https:\/\//i.test(externalUrl)) {
    return { available: true, href: externalUrl, external: true };
  }
  return { available: true, href: definition.href, external: false };
};
